import React from 'react';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Collapse,
  Divider,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ViewColumn as ViewColumnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TableColumn, ColumnSelectorConfig } from '../../../../types/columns';

// Componente para item de coluna arrastável
const DraggableColumnItem: React.FC<{
  column: TableColumn;
  onVisibilityChange: (columnId: string) => void;
  enableDragAndDrop?: boolean;
}> = ({ column, onVisibilityChange, enableDragAndDrop = true }) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(enableDragAndDrop ? attributes : {})}
      {...(enableDragAndDrop ? listeners : {})}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.5,
        borderRadius: 1,
        backgroundColor: isDragging 
          ? theme.palette.mode === 'dark' 
            ? 'rgba(25, 118, 210, 0.2)' 
            : 'rgba(25, 118, 210, 0.1)'
          : theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'transparent',
        border: isDragging 
          ? `2px dashed ${theme.palette.primary.main}` 
          : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'transparent'}`,
        boxShadow: isDragging 
          ? theme.palette.mode === 'dark'
            ? '0 8px 25px rgba(0,0,0,0.3)'
            : '0 8px 25px rgba(0,0,0,0.15)'
          : theme.palette.mode === 'dark'
            ? '0 2px 4px rgba(0,0,0,0.2)'
            : '0 2px 4px rgba(0,0,0,0.1)',
        opacity: isDragging ? 0.5 : 1,
        cursor: enableDragAndDrop ? 'grab' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.3)'
            : '0 4px 12px rgba(0,0,0,0.15)',
        },
        '&:active': {
          cursor: enableDragAndDrop ? 'grabbing' : 'default',
        },
      }}
    >
      {enableDragAndDrop && (
        <DragIcon 
          sx={{ 
            fontSize: 18, 
            color: isDragging ? theme.palette.primary.main : theme.palette.action.active,
            cursor: 'grab',
          }} 
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={column.visible}
            onChange={() => onVisibilityChange(column.id)}
            disabled={column.required}
            size="small"
            sx={{ p: 0.5 }}
          />
        }
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" color="text.primary">
              {column.label}
            </Typography>
            {column.required && (
              <Typography variant="caption" color="text.secondary">
                *
              </Typography>
            )}
          </Box>
        }
        sx={{ 
          minWidth: 'fit-content',
          '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
          m: 0
        }}
      />
    </Box>
  );
};

interface ColumnSelectorProps extends ColumnSelectorConfig {
  isExpanded: boolean;
  onToggleExpansion: () => void;
  iconOnly?: boolean;
}

export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  onColumnVisibilityChange,
  onColumnReorder,
  onResetToDefault,
  onShowAll,
  enableDragAndDrop = true,
  isExpanded,
  onToggleExpansion,
  iconOnly = false,
}) => {
  const theme = useTheme();
  const [draggedColumn, setDraggedColumn] = React.useState<TableColumn | null>(null);

  // Configuração dos sensores para drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Função para lidar com o fim do drag & drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onColumnReorder) {
      const oldIndex = columns.findIndex((col) => col.id === active.id);
      const newIndex = columns.findIndex((col) => col.id === over.id);
      
      const newColumns = arrayMove(columns, oldIndex, newIndex);
      onColumnReorder(newColumns);
    }
    
    setDraggedColumn(null);
  };

  // Função para lidar com o início do drag & drop
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const column = columns.find(col => col.id === active.id);
    if (column) {
      setDraggedColumn(column);
    }
  };

  return (
    <Tooltip title="Colunas visíveis">
    <Button
      variant="outlined"
      startIcon={iconOnly ? undefined : <ViewColumnIcon />}
      onClick={onToggleExpansion}
      size="small"
      aria-label="Colunas visíveis"
      sx={{ 
        borderColor: theme.palette.mode === 'dark' ? '#666' : '#1976d2', 
        color: theme.palette.mode === 'dark' ? '#666' : '#1976d2',
        ...(iconOnly
          ? {
              minWidth: 36,
              width: 36,
              height: 36,
              minHeight: 36,
              maxHeight: 36,
              p: 0,
              lineHeight: 1,
              aspectRatio: '1 / 1',
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiButton-startIcon': { display: 'none', m: 0 },
              '& .MuiSvgIcon-root': {
                fontSize: 20,
                width: 20,
                height: 20,
              },
            }
          : {}),
        '&:hover': {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        }
      }}
    >
      {iconOnly ? (
        <ViewColumnIcon />
      ) : (
        <>
          Colunas Visíveis
          {isExpanded ? <ExpandLessIcon sx={{ ml: 0.5 }} /> : <ExpandMoreIcon sx={{ ml: 0.5 }} />}
        </>
      )}
    </Button>
    </Tooltip>
  );
};
